import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FaUser, FaFilter, FaPhoneAlt, FaMapMarkerAlt, FaIdCard, FaUsers } from 'react-icons/fa';

import './Patients.css';
import '../AdmissionsListTable.css';
import axios from 'axios';
import ENDPOINTS, { API_BASE_URL } from '../../../Constants/ApiEndpoints';
import SearchBar from '../../Inpatient/SearchBar';
import DateRangePickerComponent from '../../../CommonComponents/DateRangePickerComponent';
import IPPatientInfoDisplay from './PatientsInfoDisplay/IPPatientInfoDisplay';
import { Pagination, PaginationItem, Box } from '@mui/material';

export default function Patients({ onSelectPatient }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);

    const [patient, setPatient] = useState(location.state?.patient || null);
    const [loadingPatient, setLoadingPatient] = useState(!patient && !!id);

    // Date range state for filtering
    const [startDate, setStartDate] = useState(new Date('1970-01-01'));
    const [endDate, setEndDate] = useState(new Date());

    const isPatientView = location.pathname.includes('/patientInfoDisplay/');

    // Load payment history if not already present on patient object
    useEffect(() => {
        const loadHistoryIfMissing = async (patientObj) => {
            if (!patientObj?.paymentPackageHistory && (patientObj?.mrNumber || id)) {
                const searchId = patientObj?.mrNumber || id;
                try {
                    // Query backend for IP-specific payment history using ?patientType=IP
                    const endpoint = `${API_BASE_URL}/getPatientPaymentHistory`;
                    const invoicePaymentsPackageresponse = await axios.get(
                        `${endpoint}/${searchId}?patientType=IP`
                    );
                    const historyData = invoicePaymentsPackageresponse.data;
                    setPatient(prev => ({
                        ...prev,
                        paymentPackageHistory: historyData
                    }));
                } catch (historyError) {
                    console.warn('Payment/package history not available:', historyError);
                }
            }
        };

        if (location.state?.patient) {
            setPatient(location.state.patient);
            setLoadingPatient(false);
            loadHistoryIfMissing(location.state.patient);
            return;
        }

        const fetchPatientById = async () => {
            try {
                setLoadingPatient(true);
                const response = await axios.get(`${API_BASE_URL}/getPatientInfoDisplayData/${id}`);
                const patientAndAppointmentData = response.data;
                setPatient(patientAndAppointmentData);
                await loadHistoryIfMissing(patientAndAppointmentData);
            } catch (error) {
                console.error('Failed to load patient data', error);
            } finally {
                setLoadingPatient(false);
            }
        };

        if (id && !location.state?.patient) {
            fetchPatientById();
        }
    }, [id, location.state]);

    // Navigate to patient detail view
    const handleSelectPatient = (record) => {
        if (onSelectPatient) {
            onSelectPatient(record);
            return;
        }
        navigate(`/inpatient/patients/patientInfoDisplay/${record.mrNumber}`, {
            state: { patient: record }
        });
    };

    const [showPatientFilter, setShowPatientFilter] = useState(false);
    const [patientFilter, setPatientFilter] = useState('');

    const [appointmentFilters, setAppointmentFilters] = useState(null);

    // Hover popover state for patient photo preview
    const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
    const [popoverDirMap, setPopoverDirMap] = useState({});

    const handleRowMouseEnter = (e, index) => {
        setHoveredRowIndex(index);
        const rect = e.currentTarget.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow < 250 && spaceAbove > 200) {
            setPopoverDirMap((prev) => ({ ...prev, [index]: 'above' }));
        } else {
            setPopoverDirMap((prev) => ({ ...prev, [index]: 'below' }));
        }
    };

    const handleRowMouseLeave = () => {
        setHoveredRowIndex(null);
    };

    // Pagination state
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const formatDate = (date) => {
        if (!date) return '';
        const pad = (n) => n.toString().padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    };

    // Fetch patient list data from API with filters (search + date range)
    const fetchData = async (currentPage = page, currentSize = pageSize) => {
        setLoading(true);

        try {
            const params = {
                fromDate: formatDate(startDate),
                toDate: formatDate(endDate),
                page: currentPage,
                size: currentSize,
            };

            if (appointmentFilters?.type && appointmentFilters?.query) {
                if (appointmentFilters.type === 'MR No') params.mrNumber = appointmentFilters.query;
                if (appointmentFilters.type === 'Name') params.name = appointmentFilters.query;
                if (appointmentFilters.type === 'Phone') params.phoneNumber = appointmentFilters.query;
            }

            const response = await axios.get(`${ENDPOINTS.GET_ALL_PATIENTS_WITH_ADMISSIONS}`, { params });
            setRecords(response.data.records || []);
            setTotalPages(response.data.totalPages || 0);
            setTotalElements(response.data.totalElements || 0);
        } catch (error) {
            console.error('Error fetching Patients data:', error);
            setRecords([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle date range change from DateRangePickerComponent
    const handleDateChange = (start, end) => {
        setStartDate(start);
        setEndDate(end);
        setPage(0);
    };

    // Re-fetch when search filters change
    useEffect(() => {
        if (appointmentFilters) {
            setPage(0);
            fetchData(0, pageSize);
        }
    }, [appointmentFilters]);

    // Fetch data on page/pageSize/date changes
    useEffect(() => {
        fetchData(page, pageSize);
    }, [page, pageSize, startDate, endDate]);

    // Client-side name filter on top of fetched records
    const displayedRecords = records.filter((record) =>
        (record.patientName || '').toLowerCase().includes(patientFilter.toLowerCase())
    );

    // Handle search bar submission
    const handleSearch = ({ type, query }) => {
        setPage(0);
        setAppointmentFilters({ type, query });
    };

    // Patient detail view rendering
    if (isPatientView) {
        if (loadingPatient) {
            return (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                    Loading patient data...
                </div>
            );
        }

        if (patient) {
            return (
                <IPPatientInfoDisplay
                    patient={patient}
                    onBack={() => navigate('/inpatient/patients')}
                />
            );
        }

        return (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                Patient Info not found.
            </div>
        );
    }

    // Main patient list view
    return (
        <div className="unified-master-card">
            {/* Top Accent Gradient Bar */}
            <div className="master-card-accent-bar" />

            {/* Master Toolbar Header: Search + Date Picker + Stats Badge */}
            <div className="master-toolbar-header">
                <div className="toolbar-left-group">
                    <div className="toolbar-search-box">
                        <SearchBar onSearch={handleSearch} />
                    </div>

                    <div className="toolbar-date-picker">
                        <DateRangePickerComponent
                            initialStartDate={startDate}
                            initialEndDate={endDate}
                            onDateChange={handleDateChange}
                        />
                    </div>
                </div>

                {/* Total patients stats badge */}
                <div className="patients-stats-badge">
                    <FaUsers size={14} color="#2563eb" />
                    <span>Total Patients: <strong>{totalElements}</strong></span>
                </div>
            </div>

            {/* Data Table Container */}
            <div className="table-container">
                {loading ? (
                    <div className="patients-loading-state">
                        <div className="spinner-border text-primary" role="status" />
                        <p>Loading Inpatient Registry...</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="master-data-table">
                            <thead>
                                <tr>
                                    <th>PATIENT ID</th>
                                    <th style={{ position: 'relative' }}>
                                        <div
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
                                            onClick={() => setShowPatientFilter((prev) => !prev)}
                                        >
                                            PATIENT NAME <FaFilter size={10} color="#64748b" />
                                        </div>
                                        {showPatientFilter && (
                                            <div className="header-filter-popover" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="text"
                                                    value={patientFilter}
                                                    onChange={(e) => setPatientFilter(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    placeholder="Filter by name..."
                                                    autoFocus
                                                />
                                            </div>
                                        )}
                                    </th>
                                    <th>MR NUMBER</th>
                                    <th>PHONE</th>
                                    <th>ADDRESS</th>
                                </tr>
                            </thead>

                            {displayedRecords.length === 0 ? (
                                <tbody>
                                    <tr>
                                        <td colSpan="5" className="empty-table-cell">
                                            No patients found matching criteria.
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                <tbody>
                                    {displayedRecords.map((record, index) => (
                                        <tr
                                            key={index}
                                            onClick={() => handleSelectPatient(record)}
                                            onMouseEnter={(e) => handleRowMouseEnter(e, index)}
                                            onMouseLeave={handleRowMouseLeave}
                                            className={`patient-row-item ${(record.flagged || record.isFlagged || record.patientFlagged) ? 'flagged-row' : ''}`}
                                        >
                                            {/* Patient ID with hover photo popover */}
                                            <td style={{ position: 'relative' }}>
                                                <span className="code-pill id-pill">
                                                    <FaIdCard size={10} /> {(record.mrNumber || '').slice(-6)}
                                                </span>

                                                {/* Dynamic Screen-Aware Hover Photo Lightbox */}
                                                {hoveredRowIndex === index && !showPatientFilter && (
                                                    <div
                                                        className={`patient-hover-popover ${popoverDirMap[index] === 'above' ? 'popover-above' : 'popover-below'}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <div className="popover-avatar-wrap">
                                                            <img
                                                                src={record.photo ? `data:image/jpeg;base64,${record.photo}` : "/VThospitalsIcon.png"}
                                                                alt={record.patientName}
                                                                className="popover-avatar-img"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </td>

                                            {/* Patient Name with avatar circle */}
                                            <td className="patient-name-cell">
                                                <div className="patient-avatar-circle">
                                                    {record.photo ? (
                                                        <img src={`data:image/jpeg;base64,${record.photo}`} alt={record.patientName} className="patient-avatar-img" />
                                                    ) : (
                                                        <FaUser size={12} color="#2563eb" />
                                                    )}
                                                </div>
                                                <span className="patient-fullname">{record.patientName}</span>
                                            </td>

                                            {/* MR Number pill */}
                                            <td>
                                                <span className="code-pill mr-pill">
                                                    {record.mrNumber}
                                                </span>
                                            </td>

                                            {/* Phone with icon */}
                                            <td>
                                                {record.phoneNumber ? (
                                                    <span className="info-cell-text">
                                                        <FaPhoneAlt size={10} color="#94a3b8" /> {record.phoneNumber}
                                                    </span>
                                                ) : (
                                                    <span className="muted-dash">-</span>
                                                )}
                                            </td>

                                            {/* Address with icon */}
                                            <td>
                                                {record.address ? (
                                                    <span className="info-cell-text text-truncate">
                                                        <FaMapMarkerAlt size={10} color="#94a3b8" /> {record.address}
                                                    </span>
                                                ) : (
                                                    <span className="muted-dash">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            )}
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination & Footer Bar */}
            <div className="master-table-footer">
                <div className="pagination-info-text">
                    Showing page <strong>{page + 1}</strong> of <strong>{totalPages || 1}</strong> ({totalElements} registered patients)
                </div>

                <Box>
                    <Pagination
                        count={totalPages}
                        page={page + 1}
                        onChange={(e, value) => setPage(value - 1)}
                        renderItem={(item) => (
                            <PaginationItem
                                {...item}
                                components={{
                                    previous: () => <span>Previous</span>,
                                    next: () => <span>Next</span>,
                                }}
                            />
                        )}
                        color="primary"
                        variant="outlined"
                        shape="rounded"
                    />
                </Box>
            </div>
        </div>
    );
}